import Typography from "@mui/material/Typography";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AsyncDialog, type AsyncDialogProps } from "../components/AsyncDialog";

export interface ConfirmDialogOptions {
  actionHandler: () => Promise<unknown>;
  cancelText: string;
  submitColor: AsyncDialogProps["submitColor"];
  submitIcon: JSX.Element;
  submitText: string;
  title: string;
}

export type ConfirmDialogContext = (
  question: string,
  options?: Partial<ConfirmDialogOptions>,
) => Promise<boolean>;

const context = createContext<ConfirmDialogContext>(async () => {
  throw new Error("No ConfirmDialogProvider present!");
});

interface ConfirmDialogRenderState {
  cancelText: string;
  question: string;
  submitColor?: AsyncDialogProps["submitColor"];
  submitIcon: JSX.Element;
  submitText: string;
  title: string;
}

type ConfirmDialogState =
  | { open: false }
  | {
      actionHandler: () => Promise<unknown>;
      open: true;
      onReject: (error: unknown) => void;
      onResolve: (result: boolean) => void;
    };

export interface ConfirmDialogProviderProps {
  children: React.ReactNode;
}

export const ConfirmDialogProvider = function ConfirmDialogProvider(
  props: ConfirmDialogProviderProps,
) {
  const { children } = props;
  const { t } = useTranslation();

  const defaultRenderState: ConfirmDialogRenderState = useMemo(
    () => ({
      cancelText: t("ConfirmDialog.cancel"),
      question: "",
      submitIcon: <></>,
      submitText: t("ConfirmDialog.ok"),
      title: t("ConfirmDialog.title"),
    }),
    [t],
  );
  const [renderState, setRenderState] =
    useState<ConfirmDialogRenderState>(defaultRenderState);
  const [state, setState] = useState<ConfirmDialogState>({ open: false });

  const handleConfirm: ConfirmDialogContext = useCallback(
    async (question, options) => {
      const {
        promise,
        reject: onReject,
        resolve: onResolve,
      } = Promise.withResolvers<boolean>();

      setRenderState({
        cancelText: options?.cancelText ?? defaultRenderState.cancelText,
        question: question ?? defaultRenderState.question,
        submitColor: options?.submitColor,
        submitIcon: options?.submitIcon ?? defaultRenderState.submitIcon,
        submitText: options?.submitText ?? defaultRenderState.submitText,
        title: options?.title ?? defaultRenderState.title,
      });
      setState({
        actionHandler: options?.actionHandler ?? (() => Promise.resolve()),
        open: true,
        onReject,
        onResolve,
      });

      return promise;
    },
    [defaultRenderState],
  );

  const handleClose = useCallback(() => {
    if (!state.open) {
      throw new Error("Illegal State!");
    }

    state.onResolve(false);
    setState({ open: false });
  }, [state]);

  const handleSubmit = useCallback(async () => {
    if (!state.open) {
      throw new Error("Illegal State!");
    }

    try {
      await state.actionHandler();

      state.onResolve(true);
    } catch (error) {
      state.onReject(error);
    }

    setState({ open: false });
  }, [state]);

  return (
    <>
      <context.Provider value={handleConfirm}>{children}</context.Provider>
      <AsyncDialog
        cancelText={renderState.cancelText}
        onClose={handleClose}
        onSubmit={handleSubmit}
        open={state.open}
        submitColor={renderState.submitColor}
        submitIcon={renderState.submitIcon}
        submitText={renderState.submitText}
        title={renderState.title}
      >
        {({ loading }) => <Typography>{renderState.question}</Typography>}
      </AsyncDialog>
    </>
  );
};

/**
 * Returns a function to spawn a confirm dialog.
 * That function returns "true", if the dialog got confirmed.
 * And "false", if it wasn't.
 */
export const useConfirmDialog = () => useContext(context);
